<template>
  <b-form @submit.prevent="onSubmit" enctype="multipart/form-data">
    <b-overlay :show="profileBootstrapLoading" rounded="sm">
      <template #overlay>
        <div class="text-center">
          <digger-loader />
        </div>
      </template>
      <b-row v-if="isPrimaryBaOrTradie" class="profile-top-row">
        <b-col cols="12" lg="4">
          <div class="page-subtitle mt-5 mb-4">
            {{ userIsTradie ? 'Profile Photo' : 'Company Logo' }}
          </div>
          <div>
            <image-cropper
              v-if="!readOnly"
              v-model="show"
              @change="uploadPhoto"
              @close="show = false"
              :title="userIsTradie ? null : 'Company Logo Upload'"
              :subtitle="userIsTradie ? null : 'Upload your Company Logo'"
              :loadingMessage="userIsTradie ? null : 'Uploading Image'"
            />
            <div
              class="upload-container mb-4"
              :class="user && user.avatar ? 'border-0' : ''"
              v-cloak
              @drop.prevent="onFileChange"
              @dragover.prevent
            >
              <b-avatar
                button
                rounded
                badge
                badge-top
                badge-offset="1.5rem"
                alt="Avatar"
                class="ml-0 mr-auto edit-avatar"
                variant="transparent"
                badge-variant="transparent"
                v-if="user && (user.avatar || !readOnly)"
                :style="'width: 240px; height: 240px;'"
                :src="user.avatar ? user.avatar.url : avatarPlaceholderUrl"
                @click="readOnly ? null : (show = true)"
              >
                <template #badge v-if="!readOnly">
                  <b-icon
                    icon="pencil-square"
                    scale="1.5"
                    class="text-light"
                  ></b-icon>
                </template>
              </b-avatar>
            </div>
          </div>
        </b-col>

        <b-col cols="12" lg="4" :class="{ 'd-none d-lg-block': !userIsTradie }">
          <b-overlay :show="loading" rounded="sm">
            <template #overlay>
              <div class="text-center">
                <digger-loader />
              </div>
            </template>
            <template v-if="userIsTradie">
              <div class="d-flex flex-column" style="gap: 0px">
                <div class="page-subtitle mt-5 mb-4">My Resume</div>
                <div
                  v-if="user.resume"
                  style="
                    width: 240px;
                    height: 240px;
                    background-color: lightgray;
                    border-radius: 16px;
                    position: relative;
                  "
                >
                  <div
                    class="position-absolute d-flex flex-column"
                    style="top: 0; right: 0; margin: 8px; gap: 8px"
                  >
                    <!-- Open -->
                    <b-btn
                      class="rounded-circle"
                      variant="success"
                      title="Preview resume"
                      style="width: 38px; height: 37px; padding: 0 0 1px 2px"
                      @click="openResume()"
                    >
                      <b-icon icon="box-arrow-up-right" size="sm" />
                    </b-btn>

                    <!-- Share -->
                    <b-btn
                      class="rounded-circle"
                      variant="primary"
                      title="Share Resume"
                      style="width: 38px; height: 37px; padding: 0 1px 2px 0"
                      @click="sharingResume()"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        class="bi bi-send"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"
                        />
                      </svg>
                    </b-btn>

                    <!-- Remove -->
                    <b-btn
                      class="rounded-circle"
                      variant="danger"
                      title="Remove Resume"
                      style="width: 38px; height: 37px; padding: 1px 0 0 0"
                      @click="removingResume()"
                    >
                      <b-icon icon="trash" size="sm" />
                    </b-btn>
                  </div>
                  <a
                    :href="resumeViewUrl"
                    target="_blank"
                    title="Click to preview PDF"
                    data-test="resume-link"
                    @click.prevent="openResume()"
                  >
                    <img
                      :src="user.resume.thumb_url || user.resume.url"
                      style="object-fit: contain; width: 100%; height: 100%"
                    />
                  </a>
                  <FilePreviewModal
                    v-if="resumePreviewOpen"
                    :visible="resumePreviewOpen"
                    :url="resumeViewUrl"
                    mime-type="application/pdf"
                    name="Resume.pdf"
                    @close="resumePreviewOpen = false"
                  />
                </div>

                <div v-else class="resume-upload">
                  <b-form @submit.prevent="uploadResume">
                    <b-form-group label="Upload Resume PDF File">
                      <b-form-file
                        v-model="resumeFile"
                        accept=".pdf"
                        class="felix mb-0"
                        required
                      />
                      <b-form-invalid-feedback class="d-block mt-0">
                        {{ resumeError }}
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <b-row>
                      <b-col class="text-right">
                        <b-btn
                          type="submit"
                          variant="primary"
                          size="sm"
                          style="height: initial"
                          class="resume-upload-btn"
                        >
                          Upload
                        </b-btn>
                      </b-col>
                    </b-row>
                  </b-form>
                </div>
              </div>

              <!-- Share Resume -->
              <b-modal v-model="isSharingResume" size="md" centered hide-footer>
                <template #modal-title>
                  <h3 class="font-weight-bold">Share Resume</h3>
                </template>
                <b-overlay :show="loading" rounded="sm">
                  <template #overlay>
                    <div class="text-center">
                      <digger-loader />
                    </div>
                  </template>
                  <b-form @submit.prevent="shareResume">
                    <b-form-group label="Share Resume via Email">
                      <b-form-input
                        v-model="shareResumeEmail"
                        placeholder="Enter Email Address"
                        required
                      />
                      <b-form-invalid-feedback
                        class="d-block mt-1"
                        style="min-height: 21px"
                      >
                        {{ shareResumeError }}
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <b-row align-h="end" class="p-2">
                      <b-button
                        variant="outline-secondary"
                        class="mx-2"
                        @click="sharingResume(false)"
                      >
                        CANCEL
                      </b-button>
                      <b-button
                        type="submit"
                        variant="primary"
                        icon-prepend="envelope"
                        class="mx-2"
                      >
                        SHARE
                      </b-button>
                    </b-row>
                  </b-form>
                </b-overlay>
              </b-modal>

              <!-- Remove Resume -->
              <b-modal
                v-model="isRemovingResume"
                size="md"
                centered
                hide-footer
              >
                <template #modal-title>
                  <h3 class="font-weight-bold">Remove Resume</h3>
                </template>
                <b-overlay :show="loading" rounded="sm">
                  <template #overlay>
                    <div class="text-center">
                      <digger-loader />
                    </div>
                  </template>
                  <b-alert
                    :show="otwWillDisableOnResumeRemoval"
                    variant="warning"
                    class="mb-3"
                  >
                    You're currently searching for work with no years of
                    experience on file. Removing your resume will turn searching
                    for work off, since companies need at least one of the two
                    to consider you.
                  </b-alert>
                  <b-row>
                    <b-col>
                      <p class="text-danger" style="opacity: 1">
                        This action cannot be undone<br />
                        you will need to reupload your Resume file
                      </p>
                    </b-col>
                  </b-row>

                  <b-row align-h="end" class="p-2">
                    <b-button
                      variant="outline-secondary"
                      class="mx-2"
                      @click="removingResume(false)"
                    >
                      CANCEL
                    </b-button>
                    <b-button
                      variant="danger"
                      class="mx-2"
                      @click="removeResume"
                    >
                      YES
                    </b-button>
                  </b-row>
                </b-overlay>
              </b-modal>
            </template>
          </b-overlay>
        </b-col>

        <b-col cols="12" lg="1" class="d-none d-lg-block"></b-col>

        <b-col cols="12" lg="3" :class="{ 'd-none d-lg-block': !userIsTradie }">
          <template v-if="userIsTradie">
            <div class="page-subtitle mt-5 mb-4">I want to:</div>
            <!-- Matches the 240px band the Profile Photo/My Resume boxes sit
                 in, so the button lines up with them instead of floating
                 near the top with dead space below it. -->
            <div class="d-flex align-items-center" style="height: 240px">
              <b-button variant="primary" @click="isSharingProfile = true">
                Share my Profile
              </b-button>
            </div>
            <ShareProfileModal
              v-if="this.user"
              :showModal="isSharingProfile"
              :defaultEmail="user.email"
              @onCloseShareProfileModal="isSharingProfile = false"
            />
          </template>
        </b-col>
      </b-row>

      <div class="page-subtitle mt-4">Personal Details</div>
      <hr class="mt-1" />
      <b-row v-if="userIsTradie">
        <b-col>
          <!-- Full Name -->
          <b-form-group>
            <label class="font-weight-bold">Full Name</label>
            <b-form-input
              id="input-full-name"
              v-model="form.full_name"
              type="text"
              required
              placeholder="Enter Full Name"
              :state="state.full_name"
              aria-describedby="input-full-name-feedback"
              :disabled="readOnly"
            ></b-form-input>

            <b-form-invalid-feedback id="input-full-name-feedback">
              <!-- eslint-disable-next-line -->
            {{ errors.full_name }}
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- Date of Birth -->
          <b-form-group v-if="userIsTradie">
            <div class="datepicker-container">
              <label class="font-weight-bold">Date of Birth</label>
              <b-form-datepicker
                id="input-date"
                v-model="form.birthdate"
                :date-format-options="{
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                }"
                :start-weekday="1"
                :max="maxBirthdate"
                :min="minBirthdate"
                locale="en-AU"
                show-decade-nav
                required
                no-flip
                placeholder="DD/MM/YYYY"
                :state="state.birthdate"
                aria-describedby="input-date-feedback"
                :disabled="readOnly"
              />
            </div>

            <b-form-invalid-feedback id="input-birth-date-feedback">
              <!-- eslint-disable-next-line -->
            {{ errors.birthdate }}
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- Address -->
          <b-form-group v-if="userIsTradie">
            <label class="font-weight-bold">Address</label>
            <b-form-input
              id="input-address"
              v-model="form.address"
              type="search"
              required
              placeholder="Enter Address"
              :state="state.address"
              aria-describedby="input-address-feedback"
              @input="onGetSuggestions"
              autocomplete="new-password"
              :disabled="readOnly"
            ></b-form-input>
            <div class="suggestions" v-if="showSuggestions">
              <ul>
                <li
                  v-for="suggestion in suggestions"
                  :key="suggestion"
                  @click="selectSuggestion(suggestion)"
                >
                  {{ suggestion }}
                </li>
              </ul>
            </div>
          </b-form-group>

          <!-- Medical Conditions -->
          <b-form-group v-if="userIsTradie">
            <label class="font-weight-bold">Medical Conditions</label>
            <MedicalConditionsPicker
              :selected.sync="medicalConditionsSelected"
              :other.sync="medicalConditionsOther"
              :disabled="readOnly"
            />
          </b-form-group>

          <!-- PPE Size -->
          <b-form-group v-if="userIsTradie">
            <label class="font-weight-bold">PPE Size (optional)</label>
            <b-form-select
              v-model="form.ppe_size"
              :options="ppeSizeOptions"
              :disabled="readOnly"
            >
              <template #first>
                <b-form-select-option :value="null">
                  Select
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col>
          <!-- USI Number -->
          <b-form-group v-if="userIsTradie">
            <label class="font-weight-bold">USI Number</label>
            <b-form-input
              id="input-usi"
              :class="state.usi ? 'with-usi' : 'with-no-usi'"
              v-model="form.usi"
              type="text"
              placeholder="Enter USI Number"
              aria-describedby="input-usi-feedback"
            >
            </b-form-input>
            <template #append>
              <b-input-group-text
                id="popover-button-sync"
                class="clickable"
                :class="state.usi ? 'with-usi' : 'with-no-usi'"
              >
                <b-icon
                  icon="info-circle"
                  variant="dark"
                  @click="readOnly ? null : (showFindUsi = !showFindUsi)"
                />
              </b-input-group-text>
              <b-popover :show.sync="showFindUsi" target="popover-button-sync">
                Find your USI Number
                <strong
                  ><b-link
                    href="https://www.usi.gov.au/students/find-your-usi"
                    target="_blank"
                    >here</b-link
                  ></strong
                >
              </b-popover>
            </template>
          </b-form-group>

          <!-- Trade -->
          <b-form-group v-if="userIsTradie">
            <label class="font-weight-bold">Trade</label>
            <b-form-input
              id="input-trade"
              v-model="form.trade"
              type="text"
              required
              placeholder="Enter Trade"
              :state="state.trade"
              aria-describedby="input-trade-feedback"
              readonly
              @click.prevent="onPickingTrade(true)"
            ></b-form-input>

            <b-form-invalid-feedback id="input-trade-feedback">
              {{ errors.trade }}
            </b-form-invalid-feedback>
          </b-form-group>
          <TradesPickerModal
            :showModal="isPickingTrade"
            :existingTrades="form.trade"
            @onCloseTradesPickerModal="onPickingTrade(false)"
            @onTradesPicked="(trades) => onTradesPicked(trades)"
          />

          <!-- Gender -->
          <b-form-group v-if="userIsTradie">
            <label class="font-weight-bold">Gender</label>
            <b-form-radio
              v-model="form.gender"
              value="male"
              :disabled="readOnly"
              >Male</b-form-radio
            >
            <b-form-radio
              v-model="form.gender"
              value="female"
              :disabled="readOnly"
              >Female</b-form-radio
            >
            <b-form-radio
              v-model="form.gender"
              :value="null"
              :disabled="readOnly"
            >
              Prefer not to say
            </b-form-radio>
          </b-form-group>

          <!-- Trainee / Apprentice? -->
          <b-form-group v-if="userIsTradie">
            <label class="font-weight-bold"
              >Are you Trainee / Apprentice?</label
            >
            <b-form-radio
              v-model="form.is_trainee"
              :value="true"
              :disabled="readOnly"
            >
              Yes
            </b-form-radio>
            <b-form-radio
              v-model="form.is_trainee"
              :value="false"
              :disabled="readOnly"
            >
              No
            </b-form-radio>
          </b-form-group>

          <!-- Aboriginal / Torres Strait Islander descent? -->
          <!-- prettier-ignore -->
          <b-form-group v-if="userIsTradie">
          <label class="font-weight-bold">Are you Aboriginal / Torres Strait Islander descent?</label>
          <b-form-radio
            v-model="form.is_aboriginal"
            :value="true"
            :disabled="readOnly"
            >Yes</b-form-radio
          >
          <b-form-radio
            v-model="form.is_aboriginal"
            :value="false"
            :disabled="readOnly"
            >No</b-form-radio
          >
        </b-form-group>
        </b-col>
      </b-row>

      <!-- BA Personal Details -->
      <b-row v-else>
        <b-col>
          <b-row>
            <!-- Full Name -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Full Name</label>
                <b-form-input
                  id="input-full-name"
                  v-model="form.full_name"
                  type="text"
                  required
                  placeholder="Enter Full Name"
                  :state="state.full_name"
                  aria-describedby="input-full-name-feedback"
                  :disabled="readOnly"
                ></b-form-input>

                <b-form-invalid-feedback id="input-full-name-feedback">
                  {{ errors.full_name }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Email -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Email</label>
                <b-form-input
                  id="input-ba-email"
                  v-model="form.email"
                  type="email"
                  required
                  placeholder="Enter Email"
                  :state="state.email"
                  aria-describedby="input-ba-email-feedback"
                  :disabled="readOnly"
                ></b-form-input>

                <b-form-invalid-feedback id="input-ba-email-feedback">
                  {{ errors.email }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Phone Number -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Phone Number</label>
                <vue-tel-input
                  id="input-ba-phone"
                  ref="baPhoneInput"
                  v-model="baPhonePrefill"
                  default-country="AU"
                  :preferred-countries="['AU']"
                  :enabled-country-code="true"
                  mode="international"
                  :disabled-fetching-country="true"
                  :disabled="readOnly"
                  :wrapper-classes="{
                    'is-invalid': state.phone_number === false,
                    'is-valid': state.phone_number === true,
                  }"
                  @validate="onBaPhoneValidate"
                  @input="onBaPhoneInput"
                  @keydown.native="blockNonDigitKey"
                  @paste.native="
                    sanitizePhonePaste($event, this, 'baPhonePrefill')
                  "
                ></vue-tel-input>

                <b-form-invalid-feedback
                  id="input-ba-phone-feedback"
                  :state="state.phone_number"
                >
                  {{ errors.phone_number }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col></b-col>
          </b-row>
        </b-col>
      </b-row>

      <div class="page-subtitle mt-5" v-if="isPrimaryBaOrTradie">
        {{ userIsTradie ? 'Emergency Contact' : 'Company Details' }}
      </div>
      <hr class="mt-1" v-if="isPrimaryBaOrTradie" />
      <!-- IF Tradie > Emergency Contact -->
      <b-row v-if="userIsTradie">
        <b-col>
          <!-- Full Name -->
          <b-form-group id="input-group-emergency_contact_full_name">
            <label class="font-weight-bold">Full Name</label>
            <b-form-input
              id="input-emergency_contact_full_name"
              v-model="form.emergency_contact_full_name"
              type="text"
              required
              placeholder="Enter Full Name"
              :state="state.emergency_contact_full_name"
              aria-describedby="input-emergency_contact_full_name-feedback"
              :disabled="readOnly"
            ></b-form-input>

            <b-form-invalid-feedback
              id="input-emergency_contact_full_name-feedback"
            >
              {{ errors.emergency_contact_full_name }}
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- Email -->
          <b-form-group id="input-group-emergency_contact_email">
            <label class="font-weight-bold">Email</label>
            <b-form-input
              id="input-emergency_contact_email"
              v-model="form.emergency_contact_email"
              type="text"
              placeholder="Enter Email"
              :state="state.emergency_contact_email"
              aria-describedby="input-emergency_contact_email-feedback"
              :disabled="readOnly"
            ></b-form-input>

            <b-form-invalid-feedback
              id="input-emergency_contact_email-feedback"
            >
              {{ errors.emergency_contact_email }}
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- Phone Number -->
          <b-form-group id="input-group-emergency_contact_phone_number">
            <label class="font-weight-bold">Phone Number</label>
            <vue-tel-input
              id="input-emergency_contact_phone_number"
              ref="emergencyContactPhoneInput"
              v-model="emergencyContactPhonePrefill"
              default-country="AU"
              :preferred-countries="['AU']"
              :enabled-country-code="true"
              mode="international"
              :disabled-fetching-country="true"
              :disabled="readOnly"
              :wrapper-classes="{
                'is-invalid': state.emergency_contact_phone_number === false,
                'is-valid': state.emergency_contact_phone_number === true,
              }"
              @input="onEmergencyContactPhoneInput"
              @keydown.native="blockNonDigitKey"
              @paste.native="
                sanitizePhonePaste($event, this, 'emergencyContactPhonePrefill')
              "
            ></vue-tel-input>

            <b-form-invalid-feedback
              id="input-emergency_contact_phone_number-feedback"
              :state="state.emergency_contact_phone_number"
            >
              {{ errors.emergency_contact_phone_number }}
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- Relationship -->
          <b-form-group id="input-group-emergency_contact_relationship">
            <label class="font-weight-bold">Relationship</label>
            <b-form-input
              id="input-emergency_contact_relationship"
              v-model="form.emergency_contact_relationship"
              type="text"
              required
              placeholder="Enter Relationship"
              :state="state.emergency_contact_relationship"
              aria-describedby="input-emergency_contact_relationship-feedback"
              :disabled="readOnly"
            ></b-form-input>

            <b-form-invalid-feedback
              id="input-emergency_contact_relationship-feedback"
            >
              {{ errors.emergency_contact_relationship }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col></b-col>
      </b-row>

      <!-- ELSE (Business Admin) > Company Details -->
      <b-row v-else>
        <b-col v-if="isPrimaryBaOrTradie">
          <b-row>
            <!-- Name -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Company Name</label>
                <b-form-input
                  id="input-name"
                  v-model="form2.name"
                  type="text"
                  required
                  placeholder="Enter Company Name"
                  :state="state2.name"
                  aria-describedby="input-name-feedback"
                  :disabled="readOnly"
                ></b-form-input>

                <b-form-invalid-feedback id="input-name-feedback">
                  {{ errors2.name }}
                </b-form-invalid-feedback>
                <b-form-text v-if="!readOnly">
                  The name people see on Tradie Pass. You can use your trading
                  name.
                </b-form-text>
              </b-form-group>
            </b-col>

            <!-- Legal name from the ABR, set by the API. Never editable here. -->
            <b-col v-if="company">
              <b-form-group>
                <label class="font-weight-bold" for="input-legal_name"
                  >Legal Name (from ABN)</label
                >
                <div class="legal-name-row">
                  <b-form-input
                    id="input-legal_name"
                    :value="company.legal_name"
                    type="text"
                    readonly
                    placeholder="Not looked up yet"
                  ></b-form-input>
                  <b-button
                    v-if="!company.legal_name && !readOnly"
                    variant="outline-primary"
                    class="legal-name-row__btn"
                    :disabled="lookingUpLegalName || !canLookUpLegalName()"
                    @click="lookUpLegalName"
                  >
                    {{
                      lookingUpLegalName ? 'Looking up…' : 'Look up from ABN'
                    }}
                  </b-button>
                </div>
                <b-form-text>
                  {{
                    company.legal_name || readOnly || canLookUpLegalName()
                      ? "Your name on the Australian Business Register. It can't be edited here."
                      : 'Save your ABN first, then look up your legal name.'
                  }}
                </b-form-text>
              </b-form-group>
            </b-col>
          </b-row>
          <!-- prettier-ignore -->
          <CompanyServicesPickerModal :showModal="isPickingCompanyServices" :existingCompanyServices="form2.description"
          @onCloseCompanyServicesPickerModal="onPickingCompanyService(false)"
          @onCompanyServicesPicked="(companyServices) => onCompanyServicePicked(companyServices)" />

          <b-row>
            <!-- Business Number -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Business Number</label>
                <b-form-input
                  id="input-business_number"
                  v-model="form2.business_number"
                  type="text"
                  required
                  placeholder="Enter Business Number"
                  :state="state2.business_number"
                  aria-describedby="input-business_number-feedback"
                  :disabled="readOnly"
                ></b-form-input>

                <b-form-invalid-feedback id="input-business_number-feedback">
                  {{ errors2.business_number }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Contact Number -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Contact Number</label>
                <vue-tel-input
                  id="input-contact_number"
                  ref="contactNumberInput"
                  v-model="contactNumberPrefill"
                  default-country="AU"
                  :preferred-countries="['AU']"
                  :enabled-country-code="true"
                  mode="international"
                  :disabled-fetching-country="true"
                  :disabled="readOnly"
                  :wrapper-classes="{
                    'is-invalid': state2.contact_number === false,
                    'is-valid': state2.contact_number === true,
                  }"
                  @validate="onContactNumberValidate"
                  @input="onContactNumberInput"
                  @keydown.native="blockNonDigitKey"
                  @paste.native="
                    sanitizePhonePaste($event, this, 'contactNumberPrefill')
                  "
                ></vue-tel-input>

                <b-form-invalid-feedback
                  id="input-contact_number-feedback"
                  :state="state2.contact_number"
                >
                  {{ errors2.contact_number }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Email -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Email</label>
                <b-form-input
                  id="input-email"
                  v-model="form2.email"
                  type="text"
                  required
                  placeholder="Enter Company Email"
                  :state="state2.email"
                  aria-describedby="input-email-feedback"
                  :disabled="readOnly"
                ></b-form-input>

                <b-form-invalid-feedback id="input-email-feedback">
                  {{ errors2.email }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Address -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Address</label>
                <b-form-input
                  id="input-address"
                  v-model="form2.address"
                  type="search"
                  required
                  placeholder="Enter Address"
                  :state="state2.address"
                  aria-describedby="input-address-feedback"
                  @input="onGetSuggestions2"
                  autocomplete="new-password"
                  :disabled="readOnly"
                ></b-form-input>
                <div class="suggestions" v-if="showSuggestions">
                  <ul>
                    <li
                      v-for="suggestion in suggestions"
                      :key="suggestion"
                      @click="selectSuggestion2(suggestion)"
                    >
                      {{ suggestion }}
                    </li>
                  </ul>
                </div>

                <b-form-invalid-feedback id="input-address-feedback">
                  {{ errors2.address }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Description - Services Provided  -->
            <b-col>
              <b-form-group>
                <label class="font-weight-bold">Services Provided</label>
                <b-form-textarea
                  rows="3"
                  id="input-description"
                  v-model="form2.description"
                  type="text"
                  required
                  placeholder="Click to select services"
                  :state="state2.description"
                  aria-describedby="input-description-feedback"
                  readonly
                  @click.prevent="
                    readOnly ? null : onPickingCompanyService(true)
                  "
                />

                <b-form-invalid-feedback id="input-description-feedback">
                  {{ errors2.description }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <!-- Actions -->
      <b-row class="my-5 pb-5">
        <!-- <b-col>
        <b-button
          block
          class="btn-outlined bg-white text-dark"
          variant="outline-secondary"
          @click="test"
          >TEST</b-button
        >
      </b-col> -->
        <b-col></b-col>
        <b-col></b-col>
        <b-col>
          <b-button
            v-if="!readOnly"
            block
            class="btn-outlined"
            type="submit"
            variant="primary"
            :disabled="loading"
          >
            <b-spinner v-if="loading" small label="Loading" />
            <div v-else>Save</div>
          </b-button>
        </b-col>
      </b-row>

      <b-alert
        class="custom-alert"
        :show="dismissCountDown"
        dismissible
        fade
        variant="primary"
        @dismiss-count-down="countDownChanged"
        >Your profile has been updated</b-alert
      >

      <b-alert
        v-model="showAvatarAlert"
        class="position-fixed fixed-top m-0 rounded-0"
        style="z-index: 2000"
        variant="danger"
        dismissible
      >
        <b>ERROR: </b>{{ avatarErrorMessage }}
      </b-alert>
    </b-overlay>
  </b-form>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import ImageCropper from '@/components/modals/ImageCropper'
import TradesPickerModal from '@/components/modals/TradesPickerModal.vue'
import ShareProfileModal from '@/components/modals/ShareProfileModal.vue'
import CompanyServicesPickerModal from '@/components/modals/CompanyServicesPickerModal.vue'
import MedicalConditionsPicker from '@/components/inputs/MedicalConditionsPicker.vue'
import FilePreviewModal from '@/components/modals/FilePreviewModal.vue'
import { VueTelInput } from 'vue-tel-input'
import _ from 'lodash'
import {
  PPE_SIZE_FALLBACK_OPTIONS,
  fetchPpeSizeOptions,
  parseMedicalConditions,
  serializeMedicalConditions,
} from '@/utils/tradieProfileFields'
import {
  MIN_TRADIE_AGE,
  latestAllowedBirthdate,
  earliestAllowedBirthdate,
  isOldEnough,
} from '@/utils/agePolicy'
import {
  cleanPhoneDigits,
  blockNonDigitKey,
  sanitizePhonePaste,
  formatPhoneForDisplay,
  installPhoneDisplayWatch,
} from '@/utils/phonePolicy'

export default {
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
    isPrimaryBusinessAdmin: {
      type: Boolean,
      default: null,
    },
  },
  components: {
    ImageCropper,
    TradesPickerModal,
    ShareProfileModal,
    CompanyServicesPickerModal,
    MedicalConditionsPicker,
    VueTelInput,
    FilePreviewModal,
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      suggestions: (state) => state.place.suggestions,
      company: (state) => state.company.company,
    }),

    ...mapState('tradieOtw', {
      otwProfile: (state) => state.profile,
    }),

    // Removing the resume would leave OTW enabled with nothing for a
    // company to judge the tradie on — ProfileController::update() auto-
    // disables OTW in exactly this case, so warn about it here before the
    // tradie confirms, rather than let it happen silently.
    otwWillDisableOnResumeRemoval() {
      return !!(
        this.userIsTradie &&
        this.otwProfile &&
        this.otwProfile.enabled &&
        !this.otwProfile.years_experience
      )
    },

    showSuggestions() {
      return this.suggestions.length > 0
    },

    userIsTradie() {
      return this.user.user_type === 'tradie'
    },

    // Prefer the signed URL named "{Name} - Resume.pdf"; fall back to the
    // bare (hash-named) media URL if the API hasn't sent resume_url.
    resumeViewUrl() {
      if (!this.user || !this.user.resume) return null
      return this.user.resume_url || this.user.resume.url
    },

    isPrimaryBa() {
      if (!this.user || this.user.user_type !== 'business_admin') return false
      // Prefer the prop computed by SettingsPage (which handles the API fallback).
      // Fall back to a loose check on the raw user field in case the prop isn't provided.
      return this.isPrimaryBusinessAdmin !== null
        ? this.isPrimaryBusinessAdmin
        : !!this.user.is_primary
    },

    /** Primary BA sections also visible to tradies (same v-if sites as former isPrimaryBa-only). */
    isPrimaryBaOrTradie() {
      return this.isPrimaryBa || this.userIsTradie
    },

    /** Date of Birth can't be more recent than the minimum age. */
    maxBirthdate() {
      return latestAllowedBirthdate()
    },

    /** Date of Birth can't be older than a sane maximum age. */
    minBirthdate() {
      return earliestAllowedBirthdate()
    },

    /**
     * PPE sizes from the API (same endpoint as the app), plus the user's
     * stored value if it matches none of them (older accounts may hold a
     * free-typed size) so the select doesn't blank out an existing value.
     */
    ppeSizeOptions() {
      const options = this.ppeSizeApiOptions.slice()
      const current = this.form.ppe_size
      if (current && !options.some((option) => option.value === current)) {
        options.push({ value: current, text: current })
      }
      return options
    },
  },

  data() {
    return {
      profileBootstrapLoading: true,
      loading: false,
      disabled: true,
      show: false,
      formattedDate: null,
      dismissCountDown: 0,
      showFindUsi: false,
      avatarPlaceholderUrl:
        'https://placehold.co/160x160?text=Click+to+add+photo',
      // vue-tel-input's v-model is the text shown/edited in the field - the
      // grouped, dial-code-free local number (see formatPhoneForDisplay()),
      // NOT the same format as form.phone_number/form2.contact_number,
      // which stay in the combined format (cc+number, no "+") the API
      // expects. baPhoneValid/contactNumberValid start `null` (untouched/
      // unknown) rather than `false`, so a BA who never touches a field
      // with an already-valid stored number isn't wrongly blocked at
      // submit - see onSubmit().
      baPhonePrefill: '',
      baPhoneValid: null,
      contactNumberPrefill: '',
      contactNumberValid: null,
      // Optional field (tradie Emergency Contact) - no *Valid flag needed
      // since it never blocks submit, same as OnboardingTradiePage.vue's
      // emergency contact field.
      emergencyContactPhonePrefill: '',
      form: {
        full_name: null,
        email: null,
        phone_number: null,
        usi: null,
        birthdate: null,
        trade: null,
        gender: null,
        address: null,
        is_trainee: null,
        medical_conditions: null,
        ppe_size: null,
        is_aboriginal: null,
        emergency_contact_full_name: null,
        emergency_contact_email: null,
        emergency_contact_phone_number: null,
        emergency_contact_relationship: null,
      },
      // Picker state behind form.medical_conditions — serialised back to the
      // backend's comma-separated string on submit.
      medicalConditionsSelected: [],
      medicalConditionsOther: '',
      // Replaced by the /ppe-sizes response once it arrives (app parity).
      ppeSizeApiOptions: PPE_SIZE_FALLBACK_OPTIONS.slice(),
      state: {
        full_name: null,
        email: null,
        phone_number: null,
        usi: null,
        birthdate: null,
        trade: null,
        gender: null,
        address: null,
        is_trainee: null,
        medical_conditions: null,
        is_aboriginal: null,
        emergency_contact_full_name: null,
        emergency_contact_email: null,
        emergency_contact_phone_number: null,
        emergency_contact_relationship: null,
      },
      errors: {
        full_name: null,
        email: null,
        phone_number: null,
        birthdate: null,
        trade: null,
        address: null,
        is_trainee: null,
        medical_conditions: null,
        is_aboriginal: null,
        emergency_contact_full_name: null,
        emergency_contact_email: null,
        emergency_contact_phone_number: null,
        emergency_contact_relationship: null,
      },
      isPickingTrade: false,
      isSharingResume: false,
      isRemovingResume: false,
      isSharingProfile: false,
      isPickingCompanyServices: false,

      showAvatarAlert: false,
      avatarErrorMessage: '',

      form2: {
        name: null,
        description: null,
        business_number: null,
        contact_number: null,
        email: null,
        address: null,
      },
      state2: {
        name: null,
        description: null,
        business_number: null,
        contact_number: null,
        email: null,
        address: null,
      },
      errors2: {
        name: null,
        description: null,
        business_number: null,
        contact_number: null,
        email: null,
        address: null,
      },

      lookingUpLegalName: false,
      resumeFile: null,
      resumePreviewOpen: false,
      resumeError: null,
      shareResumeEmail: null,
      shareResumeError: null,
    }
  },

  methods: {
    ...mapActions({
      changeAvatar: 'auth/changeAvatar',
      updateUser: 'auth/updateUser',
      getAuthenticatedUser: 'auth/getAuthenticatedUser',
      getSuggestions: 'place/getSuggestions',
      getCompany: 'company/getCompany',
      updateCompany: 'company/updateCompany',
      refreshLegalName: 'company/refreshLegalName',
      fetchOtwProfile: 'tradieOtw/fetchProfile',
    }),

    // vue-tel-input's own `validCharactersOnly` prop only skips emitting
    // updates on a bad keystroke - it doesn't stop the character reaching
    // the field, so a typed letter would sit there un-acted-on. Blocking at
    // the key level (same handler used on the other phone forms) actually
    // stops it from being typed.
    blockNonDigitKey,
    sanitizePhonePaste,

    // The library's `@validate` event only fires on mount and on true<->
    // false transitions of validity - typing an incomplete number that
    // stays invalid the whole time never re-fires it, so it's unusable
    // for live red/green feedback (that's `@input`, below, which fires
    // every keystroke). What `@validate`'s mount-time firing IS good for:
    // giving baPhoneValid/contactNumberValid a correct answer even if the
    // user never touches the field before hitting submit.
    onBaPhoneValidate(payload) {
      this.baPhoneValid = payload.isValid
    },

    onContactNumberValidate(payload) {
      this.contactNumberValid = payload.isValid
    },

    // Fires on every keystroke (unlike @validate) - this is what actually
    // drives the live red/green indicator and error message.
    onBaPhoneInput(text, phoneObject) {
      this.baPhoneValid = phoneObject.isValid
      this.state.phone_number = phoneObject.isValid
      if (phoneObject.isValid) {
        this.errors.phone_number = null
        if (phoneObject.number && phoneObject.number.e164) {
          this.form.phone_number = cleanPhoneDigits(phoneObject.number.e164)
        }
      } else {
        this.errors.phone_number = 'Enter a valid phone number.'
      }
    },

    onContactNumberInput(text, phoneObject) {
      this.contactNumberValid = phoneObject.isValid
      this.state2.contact_number = phoneObject.isValid
      if (phoneObject.isValid) {
        this.errors2.contact_number = null
        if (phoneObject.number && phoneObject.number.e164) {
          this.form2.contact_number = cleanPhoneDigits(phoneObject.number.e164)
        }
      } else {
        this.errors2.contact_number = 'Enter a valid phone number.'
      }
    },

    // Optional field (tradie Emergency Contact) - no valid flag needed
    // since it never blocks submit, same as OnboardingTradiePage.vue's
    // emergency contact field.
    onEmergencyContactPhoneInput(text, phoneObject) {
      this.state.emergency_contact_phone_number = phoneObject.isValid
      if (phoneObject.isValid) {
        this.errors.emergency_contact_phone_number = null
        if (phoneObject.number && phoneObject.number.e164) {
          this.form.emergency_contact_phone_number = cleanPhoneDigits(
            phoneObject.number.e164,
          )
        }
      } else {
        this.errors.emergency_contact_phone_number =
          'Enter a valid phone number.'
      }
    },

    ...mapMutations('place', {
      setSuggestions: 'setSuggestions',
    }),

    ...mapMutations('tradieOtw', {
      setOtwProfile: 'setProfile',
    }),

    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },

    onFileChange(e) {
      this.show = true
      this.$root.$emit('fileChange', e)
    },

    nameOnClick() {
      this.showToast(
        'Unable to update name',
        'For validation purposes, please contact support@tradiepass.com.au to change your name',
        'danger',
      )
    },
    async uploadResume() {
      this.resumeError = null

      // Mirrors the backend cap (app/Rules/PdfFile.php) — UX only, catches
      // an oversized file before wasting time/data uploading it; the server
      // enforces the real limit regardless.
      const MAX_RESUME_BYTES = 50 * 1024 * 1024
      if (this.resumeFile && this.resumeFile.size > MAX_RESUME_BYTES) {
        this.resumeError = 'PDF must not be greater than 50MB.'
        return
      }

      this.loading = true
      let data = new FormData()
      data.append('resume', this.resumeFile)

      await this.$api
        .post('auth/profile/resume', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(() => this.onSubmit())
        .catch((error) => {
          this.resumeError = error.response.data.message
          this.loading = false
        })
    },

    openResume() {
      if (this.resumeViewUrl) this.resumePreviewOpen = true
    },

    sharingResume(status = true) {
      if (status) {
        this.shareResumeEmail = null
        this.shareResumeError = null
      }

      this.isSharingResume = status
    },

    async shareResume() {
      this.shareResumeError = null
      this.loading = true

      await this.$api
        .post('auth/profile/resume/share', { email: this.shareResumeEmail })
        .then(() => {
          this.showToast(
            'Share Resume',
            `Resume shared to ${this.shareResumeEmail}`,
          )
          this.sharingResume(false)
        })
        .catch((error) => (this.shareResumeError = error.response.data.message))
        .finally(() => (this.loading = false))
    },

    removingResume(status = true) {
      if (status) this.form.resume = null
      else delete this.form.resume

      this.isRemovingResume = status
    },

    async removeResume() {
      await this.onSubmit()
      this.removingResume(false)
    },

    // The API looks up the saved ABN, so an unsaved edit must be saved first.
    canLookUpLegalName() {
      const digits = (abn) => String(abn || '').replace(/\D+/g, '')
      const saved = digits(this.company && this.company.business_number)
      return !!saved && saved === digits(this.form2.business_number)
    },

    async lookUpLegalName() {
      this.lookingUpLegalName = true
      try {
        await this.refreshLegalName(this.company.id)
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "We couldn't check this ABN right now. Please try again in a moment."
        this.showToast('Unable to look up legal name', message, 'danger')
      } finally {
        this.lookingUpLegalName = false
      }
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    test() {
      console.log(this.user.company_id)
      console.log(Object.entries(this.form))
      console.log(Object.entries(this.form2))
    },

    onGetSuggestions: _.debounce(function () {
      if (this.form.address.length > 2) this.getSuggestions(this.form.address)
    }, 300),

    selectSuggestion(suggestion) {
      this.form.address = suggestion
      this.setSuggestions([])
    },

    onGetSuggestions2: _.debounce(function () {
      if (this.form2.address.length > 2) this.getSuggestions(this.form2.address)
    }, 300),

    selectSuggestion2(suggestion) {
      this.form2.address = suggestion
      this.setSuggestions([])
    },

    resetForm() {
      // Personal Details
      this.form.full_name = this.user.full_name || null
      this.form.email = this.user.email || null
      this.form.phone_number = this.user.phone_number || null
      this.baPhonePrefill = formatPhoneForDisplay(this.user.phone_number)
      // vue-tel-input emits @validate on mount as well as on input, so
      // this resolves to the real value almost immediately - no need to
      // guess it here (a `!!stored` guess was wrong for a falsy-but-
      // "should stay untouched-not-invalid" empty value).
      this.baPhoneValid = null
      this.form.usi = this.user.usi || null
      this.form.birthdate = this.user.birthdate || null
      this.form.trade = this.user.trade || null
      this.form.gender = this.user.gender || null
      this.form.address = this.user.address || null
      this.form.is_trainee = this.user.is_trainee || false
      this.form.medical_conditions = this.user.medical_conditions || null
      this.form.ppe_size = this.normalizePpeSize(this.user.ppe_size)
      this.form.is_aboriginal = this.user.is_aboriginal || false

      // Split the stored medical-conditions string into picker selections;
      // legacy free-typed values land in the "Other" input.
      const conditions = parseMedicalConditions(this.form.medical_conditions)
      this.medicalConditionsSelected = conditions.selected
      this.medicalConditionsOther = conditions.other || ''

      // Emergency Contact
      this.form.emergency_contact_full_name =
        this.user.emergency_contact_full_name || null
      this.form.emergency_contact_email =
        this.user.emergency_contact_email || null
      this.form.emergency_contact_phone_number =
        this.user.emergency_contact_phone_number || null
      this.emergencyContactPhonePrefill = formatPhoneForDisplay(
        this.user.emergency_contact_phone_number,
      )
      this.form.emergency_contact_relationship =
        this.user.emergency_contact_relationship || null

      // Only one of these refs exists at a time (BA vs tradie template
      // branch) - installPhoneDisplayWatch() is a no-op for whichever ref
      // isn't rendered. $nextTick since resetForm() can run before the
      // user-type v-if has settled, so the ref may not exist yet.
      this.$nextTick(() => {
        installPhoneDisplayWatch(this, 'baPhoneInput')
        installPhoneDisplayWatch(this, 'emergencyContactPhoneInput')
      })

      this.scrollToTop()
    },

    resetForm2() {
      this.form2.name = this.company.name || null
      this.form2.description = this.company.description || null
      this.form2.business_number = this.company.business_number || null
      this.form2.contact_number = this.company.contact_number || null
      this.contactNumberPrefill = formatPhoneForDisplay(
        this.company.contact_number,
      )
      // vue-tel-input emits @validate on mount as well as on input, so
      // this resolves to the real value almost immediately.
      this.contactNumberValid = null
      this.form2.email = this.company.email || null
      this.form2.address = this.company.address || null

      this.$nextTick(() => {
        installPhoneDisplayWatch(this, 'contactNumberInput')
      })
    },

    async uploadPhoto(image) {
      this.showAvatarAlert = false
      this.loading = true
      let data = new FormData()
      data.append('avatar', image)

      if (this.userIsTradie) {
        await this.$api
          .post('process-avatar', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          .then(async (res) => {
            data = new FormData()
            data.append('avatar', this.processImageFromAPI(atob(res.data.data)))
            await this.changeAvatar(data)
            this.show = false
          })
          .catch((error) => {
            this.avatarErrorMessage = error.response.data.message
            this.showAvatarAlert = true
          })
          .finally(() => {
            this.$root.$emit('uploadComplete')
            this.loading = false
          })
      } else {
        await this.changeAvatar(data)
          .then((res) => (this.show = false))
          .catch((error) => {
            this.avatarErrorMessage = error.response.data.message
            this.showAvatarAlert = true
          })
          .finally(() => {
            this.$root.$emit('uploadComplete')
            this.loading = false
          })
      }
    },

    processImageFromAPI(byteString) {
      const uint8Array = new Uint8Array(byteString.length)
      for (let i = 0; i < byteString.length; i++)
        uint8Array[i] = byteString.charCodeAt(i)
      return new Blob([uint8Array], { type: 'image/jpeg' })
    },

    async onSubmit() {
      // Enforced regardless of anything else already checked on this form —
      // mirrors the same block on sign-up (OnboardingTradiePage.vue) so a
      // date of birth under the minimum age can't be set later either.
      if (this.userIsTradie && !isOldEnough(this.form.birthdate)) {
        this.state.birthdate = false
        this.errors.birthdate = `You must be at least ${MIN_TRADIE_AGE} years old.`
        this.scrollToTop()
        return
      }

      // BAs can edit their phone number on this form (tradies can't - see
      // below). Only `=== false` (the picker actively validated it and
      // rejected it) blocks - `null` means untouched/unknown and shouldn't
      // block a save the user never meant to change this field for.
      if (!this.userIsTradie && this.baPhoneValid === false) {
        this.state.phone_number = false
        this.errors.phone_number = 'Enter a valid phone number.'
        this.scrollToTop()
        return
      }

      // Same rule for the company's contact number - only BAs see/edit this
      // field (the Company Details section is BA-only), so tradies (who
      // also reach this form and can carry an unrelated, unedited form2
      // snapshot) aren't blocked by it. Company details can be completed
      // later (see the grace-period flow in OnboardingCompanyPage.vue), so
      // an empty contact number shouldn't stop saving the rest of the
      // profile either.
      if (!this.userIsTradie && this.contactNumberValid === false) {
        this.state2.contact_number = false
        this.errors2.contact_number = 'Enter a valid phone number.'
        this.scrollToTop()
        return
      }

      this.loading = true
      try {
        if (this.userIsTradie) {
          this.form.medical_conditions = serializeMedicalConditions(
            this.medicalConditionsSelected,
            this.medicalConditionsOther,
          )
        }
        // ppe_size is nullable server-side, so an empty selection is sent as
        // null — this is what allows clearing a previously saved size.
        if (this.form.ppe_size === '') {
          this.form.ppe_size = null
        }
        const payload = { ...this.form }
        if (this.userIsTradie) {
          // Tradies can't edit their phone number on this form, and echoing
          // back the stored value (often formatted with spaces) trips the
          // update endpoint's stricter phone validation — e.g. a PPE-only
          // change would 422. Don't resubmit what can't be changed here.
          delete payload.phone_number
        } else if (payload.phone_number) {
          // BAs can edit theirs — strip all non-digit formatting before
          // sending (already validated as 8-15 digits above).
          payload.phone_number = cleanPhoneDigits(payload.phone_number)
        }
        const response = await this.updateUser(payload)
        if (this.user.company_id !== undefined) {
          if (this.form2.contact_number) {
            this.form2.contact_number = cleanPhoneDigits(
              this.form2.contact_number,
            )
          }
          await this.updateCompany({
            id: this.user.company_id,
            formData: this.form2,
          })
        }
        // Mirrors ProfileController::update() auto-disabling OTW when a
        // resume is removed with no years_experience on file — update the
        // local OTW store so otwWillDisableOnResumeRemoval (and any other
        // OTW-aware UI) reflects it immediately, without a refetch.
        if (response && response.otw_disabled) {
          this.setOtwProfile({ ...(this.otwProfile || {}), enabled: false })
          this.showToast(
            'Searching for work turned off',
            "Since you removed your resume and hadn't added years of experience, we've turned searching for work off for you. Add years of experience or a new resume to turn it back on.",
            'warning',
          )
        }
        this.scrollToTop()
        this.dismissCountDown = 3
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Something went wrong while saving your profile.'
        this.showToast('Unable to save', message, 'danger')
      } finally {
        this.loading = false
      }
    },

    /**
     * Map a stored ppe_size onto the option list's submit value — the app
     * submits the lowercase value while older records may hold the display
     * label (e.g. "XL"). Unrecognised values pass through untouched.
     */
    normalizePpeSize(stored) {
      if (!stored) return null
      const needle = String(stored).trim().toLowerCase()
      if (!needle) return null
      const match = this.ppeSizeApiOptions.find(
        (option) =>
          option.value.toLowerCase() === needle ||
          option.text.toLowerCase() === needle,
      )
      return match ? match.value : String(stored).trim()
    },

    onPickingTrade(status) {
      this.isPickingTrade = status
    },

    onTradesPicked(data) {
      this.onPickingTrade(false)
      console.log(data.trades)
      this.form.trade = data.trades
    },

    onPickingCompanyService(status) {
      this.isPickingCompanyServices = status
    },

    onCompanyServicePicked(data) {
      this.onPickingCompanyService(false)
      this.form2.description = data.companyServices
    },
  },

  async created() {
    // PPE sizes come from the same public endpoint the app uses; re-normalise
    // the stored value once the real option list arrives.
    fetchPpeSizeOptions(this.$api).then((options) => {
      this.ppeSizeApiOptions = options
      this.form.ppe_size = this.normalizePpeSize(this.form.ppe_size)
    })

    // Only needed for otwWillDisableOnResumeRemoval's warning — fire-and-
    // forget, a BA has no Open to Work profile to fetch.
    if (this.userIsTradie) {
      this.fetchOtwProfile().catch(() => {})
    }

    try {
      const userPromise = !this.user
        ? this.getAuthenticatedUser()
        : Promise.resolve()

      // Company data is pre-fetched by SettingsPage; only fetch here as a fallback
      // (e.g. direct navigation without SettingsPage initialisation).
      const companyId = this.user && this.user.company_id
      const companyPromise =
        companyId && !this.company
          ? this.getCompany(companyId)
          : Promise.resolve()

      await Promise.all([userPromise, companyPromise])

      // If user wasn't in store yet, company_id is now available — fetch if still needed.
      if (!companyId && this.user && this.user.company_id && !this.company) {
        await this.getCompany(this.user.company_id)
      }
    } catch (e) {
      console.error(e)
    } finally {
      if (this.user) {
        this.resetForm()
      }
      if (this.company) {
        this.resetForm2()
      }
      this.profileBootstrapLoading = false
    }
  },

  watch: {
    // Form - Personal Details
    'form.full_name'() {
      this.state.full_name = this.form.full_name.length !== 0
      this.errors.full_name = 'This field is required'
    },

    'form.email'() {
      this.state.email = !!(this.form.email && this.form.email.length !== 0)
      this.errors.email = 'This field is required'
    },

    // form.phone_number's state/errors are driven by onBaPhoneInput() (the
    // vue-tel-input's own @input event), not a watcher here - it knows
    // the actual validity, not just a digit count.

    'form.birthdate'() {
      this.state.birthdate = this.form.birthdate.length !== 0
      this.errors.birthdate = 'This field is required'
    },

    'form.usi'() {
      this.state.usi = this.form.usi.length !== 0 ? true : null
    },

    'form.trade'() {
      this.state.trade = this.form.trade.length !== 0
      this.errors.trade = 'This field is required'
    },

    'form.address'() {
      this.state.address = this.form.address.length !== 0
      this.errors.address = 'This field is required'
    },

    'form.emergency_contact_full_name'() {
      this.state.emergency_contact_full_name =
        this.form.emergency_contact_full_name.length !== 0
      this.errors.emergency_contact_full_name = 'This field is required'
    },

    // Optional, same as on sign-up and in the API.
    'form.emergency_contact_email'(value) {
      this.state.emergency_contact_email = value ? true : null
    },

    // form.emergency_contact_phone_number's state/errors are driven by
    // onEmergencyContactPhoneInput() (the vue-tel-input's own @input
    // event), not a watcher here - see the note on 'form.phone_number'.

    'form.emergency_contact_relationship'() {
      this.state.emergency_contact_relationship =
        this.form.emergency_contact_relationship.length !== 0
      this.errors.emergency_contact_relationship = 'This field is required'
    },

    // Form 2 - Company Details
    'form2.name'() {
      this.state2.name = this.form2.name.length !== 0
      this.errors2.name = 'This field is required'
    },

    'form2.description'() {
      this.state2.description = this.form2.description.length !== 0
      this.errors2.description = 'This field is required'
    },

    'form2.business_number'() {
      this.state2.business_number = this.form2.business_number.length !== 0
      this.errors2.business_number = 'This field is required'
    },

    // form2.contact_number's state/errors are driven by
    // onContactNumberInput() (the vue-tel-input's own @input event), not
    // a watcher here - see the note on 'form.phone_number'.

    'form2.email'() {
      this.state2.email = this.form2.email.length !== 0
      this.errors2.email = 'This field is required'
    },

    'form2.address'() {
      this.state2.address = this.form2.address.length !== 0
      this.errors2.address = 'This field is required'
    },
  },
}
</script>
<style lang="scss" scoped>
.custom-select {
  background: none;
}

.legal-name-row {
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
}

.legal-name-row__btn {
  flex: 0 0 auto;
  white-space: nowrap;
  // Drop the global `.btn { height: 56px }` so the button matches the 50px input.
  height: auto;
  padding: 4px 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

// vue-tel-input ships its own box styling (thin 1px border, 3px radius) on
// its wrapper, with the inner <input> deliberately borderless - so it's the
// wrapper that needs to match .form-control's box (src/assets/scss/layouts/
// _forms.scss), not the input. Rounding each side's own corners (rather
// than overflow:hidden on the wrapper) keeps the country dropdown - an
// absolutely-positioned child of this same wrapper - from being clipped to
// the input's 50px height.
/deep/ .vue-tel-input {
  height: 50px;
  border: 2px solid #e5e5e5;
  border-radius: 8px;

  &:focus-within {
    border-color: #2c66d5;
  }

  // Same colors Bootstrap's .is-invalid/.is-valid use on every other field
  // in this form - kept explicit here since those classes aren't applied
  // (they style via .form-control, which this wrapper isn't).
  &.is-invalid {
    border-color: #dc3545;
  }

  &.is-valid {
    border-color: #28a745;
  }
}

/deep/ .vti__dropdown {
  border-radius: 6px 0 0 6px;
}

// .vti__selection is already display:flex + align-items:center (library
// default), which centers each child's box correctly - but a global
// line-height inherited onto these text spans inflates their box well
// beyond the glyph itself, so the *box* centers correctly while the *text*
// still reads as offset inside it. Collapsing line-height to 1 here (rather
// than nudging with margin, which only happens to look right at one
// zoom/font state) fixes the actual cause.
/deep/ .vti__selection {
  line-height: 1;
}

/deep/ .vti__input {
  height: 100%;
  // Otherwise inherits the global body line-height (~1.5), which sits the
  // typed number's glyph lower in its box than the +61/arrow spans right
  // next to it (fixed to line-height:1 above) - same cause, other side.
  line-height: 1;
  border: none;
  border-radius: 0 6px 6px 0;

  &::placeholder {
    color: #b2b2b2;
  }
}

/deep/ .vti__dropdown-list {
  border-radius: 8px;
  border-color: #e5e5e5;
  margin-top: 4px;
}

// ── Mobile responsiveness ───────────────────────────────────────────────
// The form is laid out as equal-width Bootstrap columns (bare `.col`) which,
// unlike the top photo/resume section (`col-12 col-lg-*`), never stack on
// their own. Below the `md` breakpoint force those bare columns full-width so
// paired fields stack vertically instead of being squished side-by-side, and
// the Save button (also a bare `.col`) spans the full width. Laptop layout is
// untouched — this only applies under 768px.
@media (max-width: 767.98px) {
  .col {
    flex: 0 0 100%;
    max-width: 100%;
  }

  // The Company Logo / Profile Photo row's negative gutter margins pull its
  // column further left than the plain section headings (so the logo sits flush
  // against the card edge). Zero the gutters here so it lines up with the rest.
  .profile-top-row {
    margin-left: 0;
    margin-right: 0;
  }
  .profile-top-row > [class*='col'] {
    padding-left: 0;
    padding-right: 0;
  }
}

// ── Responsive section spacing ──────────────────────────────────────────
// The section headers (Company Logo / Personal Details / Company Details) use
// fixed mt-5 (3rem) / mt-4 (1.5rem) gaps sized for a laptop. On smaller screens
// that leaves an oversized white gap between sections, so the spacing starts
// compact and builds up to the current desktop values as the viewport grows.
@media (max-width: 575.98px) {
  .page-subtitle.mt-5 {
    margin-top: 1.25rem !important;
  }
  .page-subtitle.mt-4 {
    margin-top: 0.75rem !important;
  }
  .page-subtitle.mb-4 {
    margin-bottom: 0.75rem !important;
  }
}
@media (min-width: 576px) and (max-width: 991.98px) {
  .page-subtitle.mt-5 {
    margin-top: 2rem !important;
  }
  .page-subtitle.mt-4 {
    margin-top: 1.15rem !important;
  }
}

// Resume upload field: the 40px right gap balances the field against the
// desktop layout, but on narrow phones it just wastes horizontal space, so
// drop it below the `sm` breakpoint and let the field use the full column.
.resume-upload {
  padding-right: 40px;
}
@media (max-width: 575.98px) {
  .resume-upload {
    padding-right: 0;
  }

  // The custom file input's "No file chosen" text gets clipped by the
  // Browse button at 320px — slightly smaller text keeps it readable.
  .resume-upload /deep/ .custom-file-label {
    font-size: 0.9rem;
  }

  // A small right-floating Upload button looks stranded on a phone —
  // span the field width like a proper mobile form action, and pull it
  // up against the file input (the reserved error line + row gap above
  // it read as dead space).
  .resume-upload-btn {
    display: block;
    width: 100%;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .resume-upload /deep/ .form-group {
    margin-bottom: 0.5rem;
  }
}

.append-icon {
  z-index: 1;
}

/deep/ .edit-avatar,
/deep/ .edit-avatar[href] {
  border-radius: 16px !important;

  &.btn:not(:disabled):not(.disabled):hover {
    .b-avatar-img {
      img {
        transform: none !important;
      }
    }
  }
}

.clickable {
  cursor: pointer;
}

.with-usi {
  background: white;
  border-color: #28a745;
}

.with-no-usi {
  background: white;
  border: 2px solid #e5e5e5;
}

/deep/ .b-form-btn-label-control.form-control {
  height: 50px;
  padding: 0;
}

/* Vertically centre the datepicker's value/placeholder inside the 50px
   control (the inner label otherwise keeps its own top padding), and keep
   the date on a single line — on narrow screens the squeezed label used to
   wrap "12/04/1990" into fragments spilling outside the box. */
/deep/ .b-form-btn-label-control.form-control > label.form-control {
  display: flex;
  align-items: center;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  padding-top: 0;
  padding-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* The validation state reserves 36px for a check icon inside the label,
     clipping the date on narrow screens; the outer control already shows
     the green state, so drop the inner icon and reclaim the room. */
  padding-right: 0.75rem !important;
  background-image: none !important;
}

/* The global .btn rule pads the calendar-icon toggle to 84px wide (32px
   each side around a 20px icon), stealing the label's room — compact it. */
/deep/ .b-form-btn-label-control > .btn {
  padding-left: 12px;
  padding-right: 12px;
}

.suggestions {
  position: absolute;
  background-color: #fff;
  border: 1px solid #ddd;
  padding: 10px;
  z-index: 999;
}

.suggestions ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.suggestions li {
  padding: 10px;
  cursor: pointer;
}

.suggestions li:hover {
  background-color: #f0f0f0;
}

/deep/ .felix .custom-file-label {
  height: 69%;
}
</style>
